import i18n from 'i18next';
import { supabase } from './supabase';

const PRISONER_FUNCTION_URL = import.meta.env.VITE_PRISONER_FUNCTION_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * 获取当前语言设置并转换为边缘函数需要的格式
 * @returns {string} 语言代码: '' 或 'en' 或 'zh'
 */
const getCurrentLanguage = () => {
  const lang = i18n.language;
  if (lang === 'zh-CN') return 'cn';
  if (lang === 'en') return 'en';
  return ''; // 默认空字符串
};

/**
 * 验证用户认证状态和激活状态
 * @param {string} walletAddress 钱包地址
 * @returns {Promise<Object>} 验证结果，包含is_valid, error_message, status_code, user_data
 */
export const verifyAuthenticatedUser = async (walletAddress) => {
  try {
    if (!walletAddress) {
      throw new Error('钱包地址不能为空');
    }
    
    // 调用函数 verify_authenticated_user
    const { data, error } = await supabase
      .rpc('verify_authenticated_user', { p_wallet_address: walletAddress });
    
    if (error) {
      console.error('验证用户状态失败:', error);
      throw new Error(error.message || '验证用户状态时发生错误');
    }
    
    if (!data || data.length === 0) {
      throw new Error('未获取到用户验证结果');
    }
    
    // 函数返回的是一个表，我们取第一行
    return data[0];
  } catch (error) {
    console.error('验证用户时出错:', error);
    throw error;
  }
};

/**
 * 使用邀请码激活用户账户
 * @param {string} invitationCode 邀请码
 * @param {string} walletAddress 钱包地址
 * @returns {Promise<Object>} 激活结果，包含success和message
 */
export const useInvitationCode = async (invitationCode, walletAddress) => {
  try {
    if (!invitationCode) {
      throw new Error('邀请码不能为空');
    }
    
    if (!walletAddress) {
      throw new Error('钱包地址不能为空');
    }
    
    // 调用函数 use_invitation_code
    const { data, error } = await supabase
      .rpc('use_invitation_code', { 
        input_code: invitationCode, 
        input_wallet_address: walletAddress 
      });
    
    if (error) {
      console.error('使用邀请码失败:', error);
      throw new Error(error.message || '使用邀请码时发生错误');
    }
    
    if (!data || data.length === 0) {
      throw new Error('未获取到邀请码处理结果');
    }
    
    // 函数返回的是一个表，我们取第一行
    return data[0];
  } catch (error) {
    console.error('使用邀请码时出错:', error);
    throw error;
  }
};

/**
 * 直接调用囚徒困境边缘函数
 * @param {Object} requestBody 请求体
 * @returns {Promise<Object>} 响应数据
 */
const invokePrisonerFunction = async (requestBody) => {

  try {
    // 直接使用绝对URL - 避免代理相关问题
    const functionUrl = PRISONER_FUNCTION_URL;
    // console.log(`发送请求到: ${functionUrl}`, requestBody);
    
    const response = await fetch(functionUrl, {
      mode: 'cors', // 加入mode参数  
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // 不需要添加其他自定义头
        'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(requestBody)
    });

    // console.log("invokePrisonerFunction")
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP错误: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(`调用服务端失败: ${error.message}`);
  }
};

/**
 * 创建新的游戏会话或获取现有会话
 * @param {string} playerId 玩家ID
 * @param {number} totalRounds 计划进行的总轮数
 * @returns {Promise<Object>} 会话数据
 */
export const createGameSession = async (playerId, totalRounds) => {
  try {
    // 准备请求体
    const requestBody = {
      playerId, 
      totalRounds, 
      action: 'start',
      language: getCurrentLanguage()
    };
    
    // console.log("发送创建会话请求:", JSON.stringify(requestBody));
    
    const data = await invokePrisonerFunction(requestBody);

    if (!data.success) {
      throw new Error(data.error || '创建会话失败');
    }

    return data;
  } catch (error) {
    console.error("创建游戏会话时出错:", error.message);
    throw error;
  }
};

/**
 * 获取游戏会话状态
 * @param {string} playerId 玩家ID
 * @param {string} sessionId 会话ID
 * @returns {Promise<Object>} 会话状态数据
 */
export const getGameStatus = async (playerId, sessionId) => {
  try {
    // 准备请求体
    const requestBody = {
      playerId: playerId,
      sessionId: sessionId || '',
      action: 'status',
      language: getCurrentLanguage()
    };
    
    // console.log("发送状态查询请求:", JSON.stringify(requestBody));
    
    const data = await invokePrisonerFunction(requestBody);

    if (!data.success) {
      throw new Error(data.error || '获取会话状态失败');
    }

    return data;
  } catch (error) {
    console.error("获取游戏状态时出错:", error.message);
    throw error;
  }
};

/**
 * 获取当前轮次的情境预览
 * @param {string} playerId 玩家ID
 * @param {string} sessionId 会话ID
 * @returns {Promise<Object>} 情境预览数据
 */
export const getScenarioPreview = async (playerId, sessionId) => {
  try {
    // 准备请求体
    const requestBody = {
      playerId: playerId,
      sessionId: sessionId || '',
      action: 'preview',
      language: getCurrentLanguage()
    };
    
    // console.log("发送情境预览请求:", JSON.stringify(requestBody));
    
    const data = await invokePrisonerFunction(requestBody);

    if (!data.success || !data.scenario) {
      throw new Error(data.error || '获取情境预览失败');
    }

    return data;
  } catch (error) {
    console.error("获取情境预览时出错:", error.message);
    throw error;
  }
};

/**
 * 进行一轮囚徒困境博弈
 * @param {string} playerId 玩家ID
 * @param {string} sessionId 会话ID
 * @param {string} choiceId - 情景Id
 * @param {string} playerChoice 玩家选择 ("合作" 或 "背叛")
 * @returns {Promise<Object>} 执行结果
 */
export const playGameRound = async (playerId, sessionId, choiceId, playerChoice) => {
  try {
    // 准备请求体
    const requestBody = {
      playerId: playerId,
      sessionId: sessionId || '',
      choiceId: choiceId,
      playerChoice: playerChoice || '合作',
      action: 'play',
      language: getCurrentLanguage()
    };
    
    // console.log("发送游戏请求:", JSON.stringify(requestBody));
    
    const data = await invokePrisonerFunction(requestBody);

    if (!data.success) {
      throw new Error(data.error || '游戏回合执行失败');
    }

    return data;
  } catch (error) {
    console.error("执行囚徒困境博弈时出错:", error.message);
    throw error;
  }
};

/**
 * 从数据库获取NFT属性数据
 * @param {string} sessionId 游戏会话ID
 * @param {Object} options 回调函数选项
 * @param {Function} options.onSuccess 成功回调
 * @param {Function} options.onError 错误回调
 * @param {Function} options.onRetry 重试回调，用于数据不存在时的重试逻辑
 * @param {Function} options.onMaxRetries 达到最大重试次数时的回调
 * @param {number} options.maxRetries 最大重试次数，默认为3
 * @param {number} options.retryDelay 重试延迟（毫秒），默认为60000（1分钟）
 * @returns {Promise<Object|null>} NFT属性数据，如果数据未生成返回null
 */
export const fetchNftAttributes = async (sessionId, options) => {
  const { 
    onSuccess, 
    onError, 
    onRetry, 
    onMaxRetries,
    maxRetries = 3,  // 默认最多重试3次
    retryDelay = 60000  // 默认间隔1分钟
  } = options || {};
  
  try {
    const { supabase } = await import('../services/supabase');
    
    const { data, error } = await supabase
      .from('nft_attributes')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    // console.log('从数据库查询NFT属性', data);
    
    if (error) throw error;
    
    if (data) {
      console.log('找到NFT属性数据:', data);
      
      // 格式化数据以匹配组件预期格式
      const formattedData = {
        attributes: {
          class: data.attributes?.[0] || 0,
          gender: data.attributes?.[1] || 0,
          courage: data.attributes?.[2] || 0,
          wisdom: data.attributes?.[3] || 0, 
          perception: data.attributes?.[4] || 0,
          dna: data.attributes?.[5] || 0
        },
        agent: {
          logic: data.agent?.[0] || 0,
          creativity: data.agent?.[1] || 0,
          morality: data.agent?.[2] || 0, 
          decisiveness: data.agent?.[3] || 0
        },
        ai_analysis: data.ai_analysis
      };
      
      // 如果提供了成功回调，则调用
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(formattedData);
      }
      
      return formattedData;
    } else {
      // 如果提供了重试回调，则调用
      if (onRetry && typeof onRetry === 'function') {
        // 返回retryCount和maxRetries，让组件知道当前重试状态
        onRetry({ retryDelay, maxRetries });
      }
      return null;
    }
  } catch (error) {
    console.error(error);
    
    // 如果提供了错误回调，则调用
    if (onError && typeof onError === 'function') {
      onError(error.message);
    }
    
    throw error;
  }
};
